import type { Price, SourceableRef } from '@misumi/shared'
import type { ChipTone } from '../../components/ui/Chip'
import type { components } from '../../lib/api/schema.gen'

/**
 * UID-42 Staging triage (screens.md §11 / interactions.md §4). Shaped to
 * match the software-engineer's in-progress `StagingTriage` schema (the
 * `GET /api/uploads/:sessionId/staging` + confirm/ignore/swap/bulk-configure/
 * dfm endpoints on the unmerged `feat/uid-41-uploads-bff` branch) field-for-
 * field, per em's explicit "keep it swap-ready to the software-engineer's
 * staging contract" — so `mockStagingService` is a drop-in target once that
 * branch merges.
 *
 * **Build note (interactions.md §4):** the source prototype has a second,
 * DEAD `pending`/`confirmed`/`rejected` row-tint implementation that isn't
 * wired to any template. This file and `StagingPage.tsx` build against the
 * live three-section model only (Identified / Matched / Files-to-resolve) —
 * do not resurrect the dead code path.
 */

export type StagedPartType = 'catalog' | 'configurable' | 'custom'

export const STAGED_PART_TYPE_LABEL: Record<StagedPartType, string> = {
  catalog: 'Buy',
  configurable: 'Configurable',
  custom: 'Make',
}

/** Part-type tag color (Fictiv Platform.dc.html:7345-7347's `typeTag`) —
 * custom=purple, catalog ("sourced")=success/teal, configurable
 * ("matched")=info/blue. Not a binary custom/everything-else split. Shared by
 * every staging row that renders a part-type chip (`IdentifiedPartRow`,
 * `StagingBomLeafRow`) so the mapping lives in one place. */
export const PART_TYPE_TONE: Record<StagedPartType, ChipTone> = {
  custom: 'purple',
  catalog: 'success',
  configurable: 'info',
}

export interface StagedConfiguration {
  readonly process: string
  readonly material: string
  readonly finish: string | null
  readonly quantity: number
  /** UID-222 #12 (additive/optional) — requested inspection collected by the
   * bulk-configure form. Open vocabulary; null clears it. */
  readonly inspection?: string | null
  /** UID-222 #12 (additive/optional) — requested certificates collected by
   * the bulk-configure form. */
  readonly certificates?: readonly string[]
}

export interface IdentifiedPart {
  readonly partId: string
  readonly name: string
  readonly drawingFilename: string | null
  /** UID-222 #9-upload (additive/optional) — S3 object key of an UPLOADED
   * drawing registered onto this part via the `register` step. Present
   * only for an uploaded/registered drawing (not for `associate`d ones). */
  readonly drawingObjectKey?: string | null
  readonly type: StagedPartType
  readonly quantity: number
  /** UID-222 #4 (additive/optional) — per-part material set independently of
   * a full bulk-configure, via `PATCH .../parts/:partId`. */
  readonly material?: string | null
  /** UID-553 (additive/optional) — a PROCESS PRESELECT, the top-level sibling
   * of `material`, set by the CSV-config projection seed when the citing BOM
   * node's stated Process resolves but its Material does not (a full
   * `configuration` is seeded when both resolve, and this stays absent). */
  readonly process?: string | null
  readonly correlationId: string | null
  readonly configuration: StagedConfiguration | null
  readonly indicativePrice: Price | null
  /** UID-188 (additive) — row-thumbnail preview URL; null until real thumbnails exist. */
  readonly thumbnailUrl?: string | null
  /** UID-425 (A5, additive/optional) — the CAD model's own filename (e.g.
   * `bracket.step`), DISTINCT from `name` (extension-stripped display title)
   * and from `drawingFilename` (the paired 2D drawing) — the design
   * (`Fictiv Platform.dc.html:3286-3289`) renders a title line and a
   * filename line as two separate things. Absent ⇒ no filename line
   * (defensive fallback for a projection that predates this field). */
  readonly cadFilename?: string | null
}

/* UID-402 Gate-5 W7 — `IDENTIFIED_MATERIAL_OPTIONS` (UID-222 #4's vocabulary
   for the "Set material…" chip's dropdown) was REMOVED. That chip was a
   configuration affordance, which ADR-0124 D1 took off this page, leaving the
   constant with no consumer. Material vocabulary for the surfaces that DO
   configure lives with them (the Part Library / RFQ configure modals, sourced
   from the manufacturing catalog). Deleted rather than left exported, so it
   cannot be mistaken for a live contract.

   UID-584 — this note previously also cited `BulkConfigureModal`'s own
   hardcoded `MATERIALS` array as such a surface. That component had been
   unreachable since UID-402 and is now deleted; its hardcoded vocabulary was
   exactly the drift this comment warns against, so it is no longer offered as
   a precedent. */

/* UID-425 (Bug 4 item 5) — `IDENTIFIED_QUANTITY_PRESETS` (UID-222 #5's preset
 * Qty choices for the identified-row dropdown) was REMOVED along with the
 * dropdown itself. The design (`Fictiv Platform.dc.html:3327`) shows a plain
 * `<input inputmode="numeric">`, not a preset picker — the control shape had
 * never been checked against the design. Quantity remains an editable review
 * field (ADR-0124's "Kept deliberately" ruling on Qty stands; only the
 * control shape changed). Deleted rather than left exported, so it cannot be
 * mistaken for a live affordance. */

export interface SourcingOption {
  readonly optionId: string
  readonly vendor: string
  readonly sku: string
  /**
   * ADR-0146 D2 — NULLABLE. A non-exact catalog CANDIDATE has NO similarity
   * score: the catalog contract carries none, so `100` would be false and any
   * other number invented. `null` means UNKNOWN and MUST render as `—` (with an
   * accessible equivalent — a bare em-dash reads as nothing, ADR-0146 D8.6),
   * the same honest-absence treatment this table already gives a null `price`
   * and a null `leadTimeDays`.
   *
   * MUST NOT be defaulted, zero-filled, or derived from string similarity — a
   * locally computed number would be OUR guess in a column the user reads as
   * the catalog's confidence (**OQ-B** owns whether a real score may ever be
   * shown). `100` stays correct for an exact hit: a stated identity, not a
   * measurement.
   */
  readonly matchPercent: number | null
  readonly price: Price | null
  readonly leadTimeDays: number | null
  readonly selected: boolean
  /**
   * UID-491 (T4, additive/OPTIONAL) — the option's descriptive title line,
   * e.g. `M3×10 socket head screw`, `… — nearest size`, `… — bulk reel`.
   * Server-sent because the suffix rules are the catalog's, not the view's
   * (`optionTitle` in `staging-triage.ts`); deriving them here would
   * re-implement business copy in a table cell.
   *
   * OPTIONAL, and its absence is a real state, not a migration gap: every
   * producer that predates T4 omits it, including the legacy flat-bucket
   * options `MatchedPartRow` renders. `MatchedSourcingPanel` therefore gates
   * its whole Part column on this field being present SOMEWHERE in the list.
   */
  readonly optionTitle?: string | null
  /**
   * UID-491 (T4, additive/OPTIONAL) — the match-quality badge
   * (`Exact`/`Similar`/`Partial`).
   *
   * NOT derivable from {@link matchPercent}, and the two must not be
   * conflated: `matchPercent` is by construction only ever `100` (a stated
   * identity) or `null` (no score exists) per ADR-0146 D2, so it cannot
   * express "similar" or "partial" at all. `matchPercent` remains the field
   * behind the Match % column and the status chip; this is an additive
   * sibling.
   */
  readonly matchQuality?: MatchQuality | null
  /**
   * UID-491 (T4, additive/OPTIONAL) — human-readable lead time, e.g.
   * `0–1 business days` or `1 week`.
   *
   * Exists because those strings are not expressible as an integer day
   * count: {@link leadTimeDays} would have to render `0d` for the first,
   * which reads as "no lead time". `leadTimeDays` is unchanged and is still
   * the fallback when this field is absent.
   */
  readonly leadTimeDisplay?: string | null
  /**
   * UID-575 (additive/OPTIONAL) — this option's OWN catalog photo, not the
   * leaf's exact hit. Mirrors `SourcingOption.imageRef` in
   * `apps/api/src/modules/parts-library/staging-triage.ts`; not yet rendered
   * per-option anywhere in this file (the dropdown still shows the matched
   * part's own thumbnail) — kept in step with the wire so a later FE change
   * can read it without a type gap.
   */
  readonly imageRef?: string | null
}

/**
 * UID-491 (T4) — a sourcing option's match-quality badge, mirroring
 * `MatchQuality` in the contract (`schema.gen.ts`) field-for-field.
 *
 * DISTINCT from {@link MatchType}, which they are easy to confuse: `MatchType`
 * describes how the PART was matched (and carries a `manual` arm for a
 * user-picked match), whereas this describes how well one sourcing OPTION
 * matches. They share two member names and mean different things, so they are
 * deliberately separate types rather than one reused union.
 */
export type MatchQuality = 'exact' | 'similar' | 'partial'

/**
 * UID-495 (T8) — the badge word rendered inside the option's quality span.
 *
 * A record (not a `.toUpperCase()`-style derivation) so the copy is stated
 * once and stays reviewable, matching this file's own `MATCH_TYPE_LABEL`
 * convention. The word is what satisfies WCAG 1.4.1 for the three-colour
 * badge — colour is reinforcement, never the sole signal — so it must never
 * be dropped in favour of the tint alone.
 */
export const MATCH_QUALITY_LABEL: Record<MatchQuality, string> = {
  exact: 'Exact',
  similar: 'Similar',
  partial: 'Partial',
}

export type MatchType = 'exact' | 'close' | 'partial' | 'manual'

export const MATCH_TYPE_LABEL: Record<MatchType, string> = {
  exact: 'Exact match',
  close: 'Close match',
  partial: 'Partial match',
  // UID-222 W2 — a user-picked catalog match (distinct from an automatic `exact`).
  manual: 'Manual match',
}

/** Match-quality tag color (Fictiv Platform.dc.html:7401-7402 —
 * `chosenMatch==='Exact'` → teal/success, anything else (partial/close) →
 * amber/warning). Not a single fixed "success" treatment regardless of
 * quality. Lifted out of `MatchedPartRow.tsx` (ADR-0136 §2) so
 * `StagingBomLeafRow`'s tree leaf can render the EXACT SAME Chip
 * tone/label the flat `MatchedPartRow` already renders for the same part —
 * one shared tone table, not a second copy that could drift. */
export const MATCH_TYPE_TONE: Record<MatchType, ChipTone> = {
  exact: 'success',
  close: 'warning',
  partial: 'warning',
  // UID-222 W2 — a user-picked catalog match is not a high-confidence automatic
  // match; treat it as needs-attention (amber), like partial/close.
  manual: 'warning',
}

export type MatchedPartStatus = 'matched' | 'confirmed' | 'ignored'

export interface MatchedPart {
  readonly partId: string
  readonly name: string
  readonly matchedCatalogItem: string
  readonly matchType: MatchType
  readonly status: MatchedPartStatus
  readonly sourcingOptions: readonly SourcingOption[]
  /** UID-188 (additive) — row-thumbnail preview URL; null until real thumbnails exist. */
  readonly thumbnailUrl?: string | null
  /** UID-222 (additive/optional) — 1-based ordinal within the Matched
   * section, server-derived (not persisted). Feeds the "BOM · line N"
   * sub-label and an Add-to-BOM request. */
  readonly bomLine?: number
  /** UID-222 (additive/optional) — desired quantity; defaults to 1. No
   * matched-quantity edit seam exists in this epic, so this always
   * surfaces 1 today. */
  readonly quantity?: number
}

export type ResolutionIssue = 'orphan-drawing' | 'no-drawing' | 'duplicate' | 'no-match'

// Labels must match Fictiv Platform.dc.html:9455-9458 exactly — do not drift.
export const RESOLUTION_ISSUE_LABEL: Record<ResolutionIssue, string> = {
  'orphan-drawing': 'Orphan drawing',
  'no-drawing': 'No matching part',
  duplicate: 'Possible duplicate',
  'no-match': 'No catalog match',
}

export interface FileToResolve {
  readonly fileId: string
  readonly filename: string
  readonly issue: ResolutionIssue
}

/**
 * ADR-0086 — one node of the staging BOM assembly tree ("Ready to add → BOM
 * parts"). Recursive via `children`; mirrors `BomLineItem`'s shape but is
 * staging-scoped and PRE-CONFIRM (no `sourceableId`, no `unitPrice`).
 * Assembly nodes group child nodes; `kind: 'part'` leaf nodes reference an
 * existing flat `IdentifiedPart`/`MatchedPart` by `partId` — the node itself
 * carries no part payload (type/catalog-match/config-status), only structure
 * (revision, build count, BOM line, quantity) plus the POSITION-SPECIFIC values
 * ADR-0086 D1 explicitly puts on the node — see `statedPartType` /
 * `statedMaterial` below and ADR-0149 Am.1 D17. Hand-declared here
 * (not imported from `schema.gen.ts`) to match this file's own convention of
 * mirroring the BFF contract field-for-field — see the file header comment.
 */
export interface StagingBomNode {
  readonly nodeId: string
  readonly kind: 'assembly' | 'part'
  readonly name: string
  /** FK into `identified[]`/`matched[]` for `kind: 'part'`; null for `kind: 'assembly'`.
   * Optional per the wire schema (`schema.gen.ts`'s `StagingBomNode`) — treat
   * absent the same as `null`. */
  readonly partId?: string | null
  /** BOM revision label ("C", "B"); null when unknown/not applicable. */
  readonly revision?: string | null
  /** ADR-0086 D4 — semantics undefined pending a product/PM glossary
   * decision; the FE tolerates `null`/absent and renders nothing (never
   * invents a meaning). */
  readonly buildCount?: number | null
  /** Per-node quantity ("Qty = 1" for assemblies; leaf part qty). Nullable. */
  readonly quantity?: number | null
  /**
   * ADR-0156 D1/D5(4) — the WORKING quantity a human has typed for this BOM
   * line (`intake.staged_bom_node.desired_quantity`, migration `1016`).
   * `quantity` above is what the CUSTOMER said (immutable, captured);
   * this is what WE are quoting (editable, written only by the node-scoped
   * quantity verb). `null`/absent means NEVER EDITED — that is load-bearing
   * for both the rollup rule (a captured-only unresolved leaf contributes 0;
   * an edited one always counts) and the render affordance (an un-edited
   * value renders as the input's PLACEHOLDER, never its filled value — R5).
   *
   * Read order for display/rollup on the **matched**, **catalog** and
   * **unresolved** arms is `desiredQuantity ?? quantity`. The **identified**
   * arm reads NEITHER node column — `IdentifiedPart.quantity` stays the
   * unconditional source of truth (ADR-0156 INVARIANT D1.1), so the two
   * records can never observably disagree.
   */
  readonly desiredQuantity?: number | null
  /** 1-based BOM line number for a leaf part; null for assemblies. Distinct
   * from `MatchedPart.bomLine` (a Matched-section display ordinal). */
  readonly bomLine?: number | null
  /**
   * ADR-0149 D4(c) + Am.1 D17/D18 — the CUSTOMER'S OWN ASSERTION about this BOM
   * LINE (`catalog` | `custom`), read out of the line's Manufacturer and
   * Part-Number cells at parse time. A POSITION-SPECIFIC value, which is why it
   * rides the node: ADR-0086 D1's second sentence puts such values on the node,
   * not the part, and two BOM lines may state different things and both be right.
   *
   * THE `stated` PREFIX IS PROVENANCE. This is NOT `IdentifiedPart.type` (the
   * sourceable kind a staged part becomes in the Library — OUR classification of
   * a file we hold, feeding `correlationId` and the config/pricing affordances).
   * Am.1 D16 retracted the earlier decision to write this into that field.
   *
   * NOT A RESOLUTION STATE (D5): a `catalog` line may be unresolved and a
   * `custom` line may have `partId` set. `catalog` is NOT a catalog MATCH and
   * must never render as one (D7).
   *
   * ABSENT/NULL RENDERS NOTHING — no chip, no placeholder, no "Unknown", no em
   * dash (D8.3 / ADR-0136 D3). That is what keeps every existing session and
   * every fixture without a manufacturer/part-number column visually identical.
   */
  readonly statedPartType?: StagedPartType | null
  /**
   * ADR-0149 D4(b) + Am.1 D17/D18 — this BOM line's `Material` cell, VERBATIM.
   *
   * NOT `IdentifiedPart.material`, and the collision is LIVE: that field is the
   * USER'S per-part material SELECTION (PATCHable, feeds pricing); this is the
   * customer's CSV cell. Different provenance and lifecycle; they may
   * legitimately differ and must never be "reconciled".
   *
   * READ-ONLY on this surface. An unresolved leaf has no `staged_file` row, so
   * `PATCH parts/:partId` would 404 — ADR-0144 D7: absent beats
   * present-and-dead-ended (Am.1 D19(3)).
   */
  readonly statedMaterial?: string | null
  /** UID-536 (T3) — `statedMaterial`'s DIRECT SIBLING: this BOM line's
   * `Process` cell, verbatim (`intake.staged_bom_node.process`).
   *
   * NOT `StagedConfiguration.process` (a human's working-configuration
   * selection) — the collision is LIVE, same class as `statedMaterial` vs
   * `IdentifiedPart.material`.
   *
   * READ-ONLY on this surface, same terms as `statedMaterial` above. */
  readonly statedProcess?: string | null
  /**
   * UID-517 item 4 — a REAL, cross-session Parts Library thumbnail for a
   * still-unresolved leaf (`partId` null, `catalogSku` null), reused from an
   * exact-name match against the account's OWN Library from a PREVIOUS
   * session (`attachCrossSessionCustomThumbnails`, apps/api). READ-TIME
   * ONLY, never persisted. Absent means "no cross-session match (or not
   * attempted)" — `StagingBomLeafRow`'s unresolved arm falls back to the
   * shared decorative placeholder, same as any other genuine miss.
   */
  readonly crossSessionThumbnailUrl?: string | null
  /**
   * ADR-0157 D3 — soft-dismiss marker for an UNRESOLVED leaf
   * (`intake.staged_bom_node.dismissed_at`, migration `1017`). `null`/absent
   * = live. Non-null = dismissed — the row STAYS rendered (D3.1.1: excluded
   * at PROJECTION time server-side is what makes the row disappear from a
   * *live* triage read; a client that already holds this node in memory
   * must not filter it out itself, or Undo would have nothing to render a
   * control onto). Never meaningful for `kind: 'assembly'` (1017's own CHECK
   * constraint forbids it) and never meaningful for an identified/matched
   * (resolved) leaf — D3 is unresolved-only; Remove stays a hard delete
   * there.
   */
  readonly dismissedAt?: string | null
  /** UID-497 (T10) — the material a HUMAN CHOSE on this BOM line
   * (`intake.staged_bom_node.selected_material`); absent/null = never chosen.
   *
   * DISTINCT from `statedMaterial` above, which stays the customer's own CSV
   * cell, verbatim. The row renders the chosen material when set and the stated
   * one otherwise; the two may legitimately differ and are never reconciled. */
  readonly selectedMaterial?: string | null
  /** UID-497 (T10) — display filename of the drawing attached to THIS BOM line
   * (`intake.staged_bom_node.drawing_filename`); absent/null = no drawing,
   * which is the row's `drawMissing` "needs attention" state on a Make part.
   *
   * One field for all three sources (associated pool drawing, uploaded PDF,
   * generated demo drawing) — the client cannot tell them apart and does not
   * need to. The S3 key of an uploaded one is deliberately NOT on the wire. */
  readonly drawingFilename?: string | null
  readonly children: readonly StagingBomNode[]
}

/** UID-255 — the closed set of auto-quote reason codes (design refresh §7
 * screen 5). Mirrors `StagingRowSignal.reason`'s auto-quote branch
 * (`schema.gen.ts`). */
export type AutoQuoteReason = 'catalog-match' | 'catalog-part' | 'configured'

/** UID-255 — the closed set of needs-manual-attention reason codes. Mirrors
 * `StagingRowSignal.reason`'s manual branch (`schema.gen.ts`). */
export type ManualAttentionReason = 'needs-configuration' | 'ambiguous-match' | 'needs-resolution'

export type AnalysisSignalReason = AutoQuoteReason | ManualAttentionReason

export type AnalysisSignalDisposition = 'auto-quote' | 'needs-manual-attention'

/** UID-255 — human-readable label for each closed `reason` code (frontend
 * judgment call — not specified verbatim by the design handoff; flagged for
 * design review). Doubles as the per-row tooltip/label text so disposition
 * is never conveyed by tint color alone (accessibility-wcag skill). */
export const ANALYSIS_SIGNAL_REASON_LABEL: Record<AnalysisSignalReason, string> = {
  'catalog-match': 'Matched to catalog — ready to auto-quote',
  'catalog-part': 'Catalog part — ready to auto-quote',
  configured: 'Configured — ready to auto-quote',
  'needs-configuration': 'Needs configuration',
  'ambiguous-match': 'Match is ambiguous',
  'needs-resolution': 'Needs resolution',
}

/** UID-255 — per-row auto-quote-vs-manual classification (design refresh §7
 * screen 5). Mirrors `StagingRowSignal` (`schema.gen.ts`) field-for-field,
 * per this file's convention of hand-declaring types that shadow the wire
 * contract. */
export interface StagingRowSignal {
  /** partId for identified/matched rows, fileId for unresolved rows. */
  readonly id: string
  readonly section: 'identified' | 'matched' | 'unresolved'
  readonly disposition: AnalysisSignalDisposition
  readonly reason: AnalysisSignalReason
}

/** UID-255 — the batch-level "system is thinking" signal (design refresh §7
 * screen 5): the auto-quote vs. needs-manual-attention split plus the
 * per-row breakdown. Invariant (server-guaranteed): `autoQuote +
 * needsManualAttention === rows.length`. Meaningful at any
 * `analysisProgress` — the FE binds it whenever present, not gated on
 * analysis being "done". */
export interface StagingAnalysisSignal {
  readonly autoQuote: number
  readonly needsManualAttention: number
  readonly rows: readonly StagingRowSignal[]
}

export interface StagingTriage {
  readonly sessionId: string
  /** UID-425 (A5, additive/optional) — the upload session's human-readable
   * name, so the Triage heading renders it instead of the raw `sessionId`
   * UUID (`Fictiv Platform.dc.html:2978`'s `{{ sessName }}`). Absent on a
   * projection that predates this field — the heading falls back to the raw
   * id rather than printing `undefined`. */
  readonly sessionName?: string
  /** UID-425 (Gap 2, additive/optional) — the project this upload session is
   * filed under. `null`/absent = UNFILED. Safe to disclose to any caller who
   * can read the session (already returned, tenant-scoped, for every session
   * by `GET /uploads/sessions`); the NAME is gated separately below. */
  readonly workspaceId?: string | null
  /** UID-425 (Gap 2, additive/optional) — the filed project's human-readable
   * name, read from the real workspace record. MEMBERSHIP-GATED (ADR-0114
   * D6): present ONLY when the calling user holds an active membership on
   * `workspaceId`. Omitted for a non-member, a nonexistent workspace, or a
   * failed read — the three are deliberately indistinguishable, so this can
   * never be used to probe workspace existence.
   *
   * CRITICAL: absence means "no name available to you", NOT "unfiled" — use
   * `workspaceId` for the unfiled question, never the presence of this field. */
  readonly workspaceName?: string
  readonly identified: readonly IdentifiedPart[]
  readonly matched: readonly MatchedPart[]
  readonly filesToResolve: readonly FileToResolve[]
  /** UID-187 (additive) — server-derived header count roll-ups. */
  readonly summary?: StagingSummary
  /** UID-222 #11 (additive/optional) — server-authoritative identification/
   * matching progress, 0-100 (nullable). Real value from the BFF; no more
   * client-side trickle. */
  readonly analysisProgress?: number | null
  /** UID-255 (additive/optional) — the "system is thinking" auto-quote-vs-
   * manual signal. Present on every triage response once the BFF ships it
   * (initial load and after every mutation); absent is a defensive
   * fallback for older fixtures/responses, not an expected steady state. */
  readonly analysisSignal?: StagingAnalysisSignal | undefined
  /** ADR-0086 (additive/optional) — the captured BOM assembly tree for the
   * "Ready to add → BOM parts" group. Absent/empty when the session captured
   * no BOM structure — the FE then renders the ADR-0079 flat table. */
  readonly bomHierarchy?: readonly StagingBomNode[]
}

/** UID-187 file-makeup counts for the staging header. */
export interface StagingFileMakeup {
  readonly total: number
  readonly cadModels: number
  readonly drawings: number
  readonly additional: number
  readonly toResolve: number
}

/** UID-187 part-type-makeup counts for the staging header. */
export interface StagingPartMakeup {
  readonly custom: number
  readonly standard: number
  readonly configurable: number
}

/** UID-187 server-derived count roll-ups for the staging header. */
export interface StagingSummary {
  readonly files: StagingFileMakeup
  readonly partTypes: StagingPartMakeup
}

/**
 * UID-425 (A6, ADR-0130 D3) — one part `confirm-parts` just materialised into
 * the Parts Library, carried WITH the full minted `SourceableRef` so the
 * client needs no adapter and re-derives NOTHING (no `kind`/`uom`/`accountId`
 * re-decision — the server already ran `makeSourceableRef` and enforced its
 * invariants). Field names deliberately mirror `RfqBundleItem`
 * (`lib/rfq/rfq-bundle-context.tsx`) exactly so a `ConfirmedStagedPart[]` can
 * be passed straight to `rfqBundle.addItems`.
 */
export interface ConfirmedStagedPart {
  readonly sourceable: SourceableRef & {
    /** Present ONLY for a confirmed catalog match. ALWAYS absent for an
     * identified custom/configurable part — a line that still needs
     * configuration is not priced (ADR-0125 D2); confirm mints no
     * client-trusted price (ADR-0066). */
    readonly indicativePrice?: Price
    /** Present for an identified part as `{ mode: 'quote_required' }`, so the
     * RFQ line renders needs-configuration explicitly rather than inferring
     * intent from an absent price. */
    readonly pricingProjection?: components['schemas']['PricingProjection']
  }
  readonly name: string
  /** The catalog SKU for a confirmed catalog match; `null` for an uploaded
   * custom/configurable part (deliberately null, never an invented
   * placeholder). */
  readonly sku: string | null
  /** UID-553 — the part's quantity, mirroring `RfqBundleItem.quantity` so
   * `addItems` seeds the RFQ line's own quantity instead of defaulting to 1. */
  readonly quantity: number
  /** UID-553 — the CSV-seeded (or user-edited) staging configuration, or `null`
   * when the line has none. `StagingPage`'s Create-RFQ hand-off maps this to the
   * `RfqBundleItem.specSeed` that pre-populates the RFQ Configure step. */
  readonly configuration: StagedConfiguration | null
}

/**
 * UID-425 (Gap 2 of ADR-0130 D1) — whether the parts just confirmed were
 * filed under the session's project, and which project that was.
 *
 * Carries NO reason, deliberately: the reason a link did not happen may be
 * "the confirming caller holds no active membership on that workspace", and a
 * workspace that does not exist produces the IDENTICAL failure. Reporting a
 * reason would either disclose which of the two it was, or add a
 * membership-vs-infrastructure distinction the client cannot act on
 * differently — the user-facing message is the same either way. The
 * discriminated reason is server-log-only, for operators (ADR-0107 /
 * ADR-0114 D6 no-leak, ADR-0114 D7 "degrade rather than 500").
 */
export interface ConfirmProjectLinkOutcome {
  /**
   * `not_requested` — the session is UNFILED, so there was nothing to link.
   *   NOT a problem; show no warning.
   * `linked` — the link was attempted and SUCCEEDED for every confirmed part.
   * `not_linked` — the session IS filed, but the link did NOT complete for
   *   every confirmed part. The parts ARE in the Library; they are NOT
   *   reliably filed under the project. MUST warn.
   */
  readonly status: 'not_requested' | 'linked' | 'not_linked'
  /** The project the link targeted — `null` iff `status` is `not_requested`.
   * The workspace NAME is deliberately NOT here — see
   * `StagingTriage.workspaceName` for the membership-gated name. */
  readonly workspaceId: string | null
}

/** UID-191 confirm result — the updated triage + the Library ids written. */
export interface ConfirmStagedPartsResult {
  readonly triage: StagingTriage
  readonly confirmedLibraryPartIds: readonly string[]
  /** UID-425 (A6) — the minted sourceables for BOTH populations (identified +
   * matched), field-for-field `RfqBundleItem`. Required (not optional): there
   * is no `?? []` branch here whose failure mode is a silently empty RFQ. */
  readonly confirmedParts: readonly ConfirmedStagedPart[]
  /** UID-425 (Gap 2) — REQUIRED for the same reason `confirmedParts` is: an
   * optional field's failure mode here is that ABSENCE SILENTLY MEANS
   * SUCCESS, which is exactly the bug it exists to fix (the confirm path
   * failed closed on the link write but used to report unqualified success
   * on the wire). */
  readonly projectLink: ConfirmProjectLinkOutcome
}

/** UID-191 — one Library part to add to a BOM as a line (Add-to-BOM). */
export interface BomPartLine {
  readonly sourceableId: string
  readonly name: string
  readonly quantity: number
}

/** Mirrors `BulkConfigureRequest`. `inspection`/`certificates` (UID-222 #12)
 * are now real, additive/optional fields on the frozen contract — they are
 * sent through to the BFF and persisted onto each configured part's
 * `StagedConfiguration`, no longer dropped client-side. */
export interface BulkConfigureRequest {
  readonly partIds: readonly string[]
  readonly process: string
  readonly material: string
  readonly finish: string | null
  readonly quantity: number
  readonly inspection?: boolean
  readonly certificates?: string
}

/** UID-190 — files-to-resolve actions, now backed by a real, server-authoritative
 * `POST /uploads/{sessionId}/staging/resolve` endpoint (was previously mocked via
 * an FE optimistic-strip). `assign` requires a `targetPartId`. */
export type FileResolutionAction = 'assign' | 'create' | 'match' | 'keep-as-custom' | 'remove'

/** UID-188/UID-222 #9 — associated-drawing action on an identified part.
 * `associate` attaches an existing files-to-resolve drawing (real);
 * `upload` mints a real presigned S3 PUT for a NEW drawing file, and
 * `register` (called after the browser's PUT completes) HeadObject-verifies
 * the upload landed and associates it onto the part (real, two-step
 * UID-222 #9 journey); `generate` remains a demo stub (501, "coming soon"). */
export type DrawingAction = 'associate' | 'generate' | 'upload' | 'register'

/** UID-222 #9-upload — a short-lived presigned PUT ticket for a new staged
 * drawing, mirroring `CadUploadTicket` (the browser PUTs bytes to `url`
 * directly, replaying `headers` verbatim; see `s3Upload.ts`). */
export interface CadUploadTicket {
  readonly uploadId: string
  readonly objectKey: string
  readonly url: string
  readonly method: 'PUT'
  readonly headers: Readonly<Record<string, string>>
}

/** UID-222 #4 — partial edit of an Identified part's Qty and/or material.
 * Both fields optional; the caller must set at least one. */
export interface UpdateStagedPartPatch {
  readonly quantity?: number
  readonly material?: string | null
}

/** UID-222 #3 (ADR-0066, PR #154) — a NON-AUTHORITATIVE, illustrative price for
 * a DEMO catalog suggestion. Deliberately NOT a {@link Price}: it carries NO
 * `provenance`, so a demo price can never masquerade as genuine `catalog`
 * pricing authority. Only `amount`/`currency` — enough for the picker to render
 * a "$X.XX" hint. Never persisted. */
export interface IllustrativePrice {
  readonly amount: string
  readonly currency: string
}

/** UID-222 #3 — one MOCK/DEMO catalog suggestion for the "Match to catalog"
 * picker. NOT a real pricing source — `indicativePrice` is an illustrative
 * (provenance-less) hint only and is never persisted into the Library. */
export interface CatalogSuggestion {
  readonly id: string
  readonly mpn: string
  readonly name: string
  readonly vendor: string
  readonly indicativePrice: IllustrativePrice | null
  readonly thumbnailUrl: string | null
}

export interface CatalogSearchResult {
  readonly suggestions: readonly CatalogSuggestion[]
}

/** UID-222 #1 — one row in the Add-to-BOM picker's list (`GET /boms`). */
export interface BomSummary {
  readonly id: string
  readonly name: string
  readonly partCount: number
}

/** UID-255 — the composite `(section, id)` map key. `partId`/`fileId`
 * namespaces don't collide across sections today, but keying by `id` alone
 * would let a future collision (the same id appearing in two sections)
 * silently overwrite one row's signal in the index (`Map` last-write-wins).
 * The ` ` delimiter cannot occur in a section literal or a real
 * part/file id, so the composite key is unambiguous. Callers MUST read the
 * map only through {@link lookupAnalysisSignal}, never `.get(id)` directly. */
function analysisSignalKey(section: StagingRowSignal['section'], id: string): string {
  return `${section} ${id}`
}

/** UID-255 — index `StagingAnalysisSignal.rows` by their `(section, id)` once,
 * so every row-rendering component does an O(1) lookup instead of a `.find`
 * per row per render. Keyed by the composite so a cross-section id collision
 * keeps both rows' signals (see {@link analysisSignalKey}). Returns
 * `undefined` when `signal` itself is absent (defensive fallback — callers
 * treat a missing map entry and a missing map identically: no tint/label). */
export function indexAnalysisSignalRows(
  signal: StagingAnalysisSignal | undefined,
): ReadonlyMap<string, StagingRowSignal> | undefined {
  if (!signal) return undefined
  return new Map(signal.rows.map((row) => [analysisSignalKey(row.section, row.id), row]))
}

/** UID-255 — look up a row's signal by its `(section, id)`. The section is
 * part of the key (not a post-hoc re-check), so a row from another section
 * with a colliding id can never be returned here. */
export function lookupAnalysisSignal(
  signalById: ReadonlyMap<string, StagingRowSignal> | undefined,
  section: StagingRowSignal['section'],
  id: string,
): StagingRowSignal | undefined {
  return signalById?.get(analysisSignalKey(section, id))
}

/**
 * UID-402 (ADR-0124) — the row's background-wash class for its analysis
 * signal, or `undefined` for no wash. The ONE place all three flat row
 * components decide their tint, so they cannot drift.
 *
 * **The orange wash is scoped to NEEDS-RESOLUTION rows only.** Before UID-402
 * every `needs-manual-attention` row got `--color-warning-bg`, which meant an
 * identified part that merely needed configuration, and a matched part with an
 * ambiguous match, both read as urgent-orange alongside the genuinely
 * unresolved files. The IMTS Cleanup Bash review called this out: orange is the
 * "Needs resolution" section's colour and must mean that and nothing else.
 *
 * The signal itself is NOT suppressed for those rows — every row still renders
 * its `ANALYSIS_SIGNAL_REASON_LABEL` `Chip`, which is the real, non-colour
 * signal (accessibility-wcag: never convey status by colour alone). Only the
 * full-row wash is narrowed, so "orange" regains a single meaning.
 *
 * Note this is deliberately keyed on `reason`, not `section`: `needs-resolution`
 * is the reason code the server assigns to exactly the `filesToResolve` rows
 * (`buildStagingAnalysisSignal`), so the two agree today — but the reason is the
 * semantic fact ("this needs resolving"), whereas the section is a layout
 * accident, and a future section that also carries genuine resolution work
 * should tint without needing a change here.
 */
export function stagingRowSignalTintClass(
  signal: StagingRowSignal | undefined,
): string | undefined {
  // Gate-5 N7 — the two tints deliberately key off DIFFERENT fields, in this
  // order, and the asymmetry is intentional rather than an oversight:
  //
  //   green  keys on `disposition` — "the system can price this" is exactly what
  //          auto-quote means, and EVERY auto-quote reason earns the same
  //          reassurance. Nothing is gained by enumerating them.
  //   orange keys on `reason`      — "needs manual attention" is too broad to
  //          colour (that was the UID-402 defect); only `needs-resolution`
  //          earns orange, so this branch must discriminate.
  //
  // Order matters only defensively: the server never pairs `auto-quote` with
  // `needs-resolution`, so no input reaches both branches today. Checking
  // disposition first means that if such a row ever appeared it would read as
  // priceable rather than as blocking work — the safer of the two wrong
  // answers, since the CTA is derived independently and would still gate.
  if (!signal) return undefined
  if (signal.disposition === 'auto-quote') return 'staging-row-signal-auto-quote'
  return signal.reason === 'needs-resolution' ? 'staging-row-signal-needs-manual' : undefined
}

export class StagingSessionNotFoundError extends Error {
  constructor(sessionId: string) {
    super(`Staging session "${sessionId}" was not found`)
    this.name = 'StagingSessionNotFoundError'
  }
}
